/**
 * TNK-154 — operator workbench theme picker.
 *
 * Per-operator preference (stored in `appPreferences.theme`). Three
 * choices: Light, Dark, System. System follows `prefers-color-scheme`
 * via the matchMedia subscription in `useTheme`. Storefront (/shop/*)
 * keeps its own dark+gold scheme — see `styles/shop.theme.css`.
 */
import { createFileRoute } from "@tanstack/react-router";
import { Monitor, Moon, Sparkles, Sun, SunDim } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { useApp, type Theme } from "@/context/AppContext";

export const Route = createFileRoute("/settings/display")({
  component: DisplayPage,
});

interface ThemeOption {
  value: Theme;
  label: string;
  description: string;
  Icon: typeof Sun;
}

// Ordered light-family → dark-family → system so the picker groups
// related moods next to each other instead of jumping black/white.
const THEME_OPTIONS: ThemeOption[] = [
  {
    value: "light",
    label: "Light",
    description: "White surfaces, black text. Good for daylight sessions.",
    Icon: Sun,
  },
  {
    value: "ivory",
    label: "Ivory",
    description:
      "Warm dim off-white. Softer than Light for long daytime sessions.",
    Icon: SunDim,
  },
  {
    value: "dark",
    label: "Dark",
    description: "The default — black surfaces, light text. Easy on the eyes.",
    Icon: Moon,
  },
  {
    value: "midnight",
    label: "Midnight",
    description:
      "Deep purple base with softer accents. Late-night mood; still dark-mode-readable.",
    Icon: Sparkles,
  },
  {
    value: "system",
    label: "System",
    description:
      "Follow your OS — switches automatically when the OS toggles dark mode.",
    Icon: Monitor,
  },
];

function DisplayPage() {
  const { appPreferences, updateAppPreferences } = useApp();
  const current: Theme = appPreferences.theme ?? "system";

  const pick = (next: Theme) => {
    if (next === current) return;
    void updateAppPreferences({ theme: next });
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/settings" }} label="Settings" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Display</h1>
        <p className="text-sm text-muted-foreground">
          Theme is per-operator and persists across devices once you sign in.
          The public storefront keeps its own look.
        </p>
      </div>

      <div className="space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Theme
        </h2>
        <ul className="space-y-2">
          {THEME_OPTIONS.map(({ value, label, description, Icon }) => {
            const active = current === value;
            return (
              <li key={value}>
                <button
                  type="button"
                  onClick={() => pick(value)}
                  aria-pressed={active}
                  className={`flex w-full items-start gap-3 rounded-md border bg-card p-3.5 text-left transition-colors ${
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/40 hover:bg-card/80"
                  }`}
                >
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="text-sm font-semibold text-foreground">
                      {label}
                    </div>
                    <div className="text-xs leading-snug text-muted-foreground">
                      {description}
                    </div>
                  </div>
                  {active && (
                    <span className="mt-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                      Active
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
